import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { SseClient } from 'ngx-sse-client';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
/**
 * This service wraps ngx-sse-client for Server-Sent Events (SSE) streaming.
 * All SSE streaming requests should use this wrapper.
 */
export class HttpStreamService {
  /**
   * The server base url
   * @private
   */
  protected _baseUrl = `${environment.api_base_url}/${environment.api_version}`;

  /**
   * The beta server base url
   * @private
   */
  protected _betaBaseUrl = `${environment.api_base_url}/${environment.api_beta_version}`;

  /**
   * The base headers for each http request.
   * @private
   * @readonly
   */
  private readonly _headers: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  constructor(private readonly _sseClient: SseClient) {}

  /**
   * @public Generic method to make a POST request with SSE streaming response.
   * @template TBody Type of the body param, an array or an object
   * @template TEvent Type of the streamed events
   * @param {string} endpoint The endpoint to call
   * @param {TBody} body The request body
   * @param {HttpHeaders | undefined} headers Specific headers to add to the http request
   * @param {boolean} beta Whether to use beta API version
   * @returns an Observable{TEvent} with streamed events of the generic type received
   */
  public postStream<TBody, TEvent>(
    endpoint: string,
    body: TBody,
    headers?: HttpHeaders,
    beta = false
  ): Observable<TEvent> {
    const url = `${beta ? this._betaBaseUrl : this._baseUrl}/${endpoint}`;
    const mergedHeaders = this._mergeHeaders(headers);

    return this._sseClient
      .stream(
        url,
        {
          keepAlive: false, // Don't auto-reconnect for single requests
          responseType: 'event',
        },
        {
          headers: mergedHeaders,
          body: body, // Angular HttpClient will serialize this to JSON
        },
        'POST' // HTTP method
      )
      .pipe(
        map((event: Event) => {
          // Cast to MessageEvent and parse the SSE data field
          const messageEvent = event as MessageEvent;
          return JSON.parse(messageEvent.data) as TEvent;
        })
      );
  }

  /**
   * Creates a new HttpHeaders based on the baseHeaders and the received headers of the method
   * @param {HttpHeaders | null} headers The headers to merge
   * @returns the HttpHeaders to send to the request
   */
  private _mergeHeaders(headers?: HttpHeaders): HttpHeaders {
    if (!headers) {
      return this._headers;
    }

    let newHeaders: HttpHeaders = new HttpHeaders();

    for (const key in this._headers.keys()) {
      const headerValue = this._headers.get(key);
      if (headerValue !== null) {
        newHeaders = headers.append(key, headerValue);
      }
    }

    for (const key in headers.keys()) {
      const headerValue = headers.get(key);
      if (headerValue !== null) {
        newHeaders = headers.append(key, headerValue);
      }
    }
    return newHeaders;
  }
}
